import React, { useState } from 'react';
import { api_srv } from 'services';
import { useQuery } from 'react-query';
import { requestSettlement } from 'services/apiSrv';
import { show_toast } from 'utils/helpers';
import { ModalContext } from 'context/ModalContext';
import { useContext } from 'react';

function RequestSettlement() {
  const { modalClose } = useContext(ModalContext);

  const [formData, setFormData] = useState({
    wallet: '',
    amount: '',
    remarks: '',
    channel: 'mobile', // 'mobile' or 'bank'
    mobileType: 'phone', // 'phone', 'paybill', 'till'
  });

  const [mobileDetails, setMobileDetails] = useState({
    phoneNumber: '',
    businessNo: '',
    accountNo: '',
    tillNo: '',
  });

  const [bankDetails, setBankDetails] = useState({
    bankAccount: '',
    bankName: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: wallets = [], isLoading } = useQuery({
    queryKey: ['wallet'],
    queryFn: async () => {
      const resp = await (await api_srv).fetch_wallet_info();
      return resp.data || [];
    },
  });

  const selectedWallet = wallets.find(w => w.accountNumber === formData.wallet);
  const availableBalance = selectedWallet?.balance ?? selectedWallet?.accountBalance ?? 0;

  const validateForm = () => {
    const errors = {};

    if (!formData.wallet) {
      errors.wallet = 'Please select a wallet';
    }

    const amountNum = Number(formData.amount);
    if (!formData.amount || isNaN(amountNum) || amountNum <= 0) {
      errors.amount = 'Please enter a valid amount greater than 0';
    } else if (amountNum > availableBalance) {
      errors.amount = `Amount exceeds available balance (KES ${Number(availableBalance).toLocaleString()})`;
    }

    // Mobile validation
    if (formData.channel === 'mobile') {
      if (formData.mobileType === 'phone') {
        if (!mobileDetails.phoneNumber.trim()) {
          errors.phoneNumber = 'Phone number is required';
        } else if (!/^(0|\+254|254)?[17]\d{8}$/.test(mobileDetails.phoneNumber.replace(/\s/g, ''))) {
          errors.phoneNumber = 'Invalid Kenyan phone number format';
        }
      } else if (formData.mobileType === 'paybill') {
        if (!mobileDetails.businessNo.trim()) {
          errors.businessNo = 'Business number is required';
        }
      } else if (formData.mobileType === 'till') {
        if (!mobileDetails.tillNo.trim()) {
          errors.tillNo = 'Till number is required';
        }
      }
    }

    // Bank validation
    if (formData.channel === 'bank') {
      if (!bankDetails.bankAccount.trim()) {
        errors.bankAccount = 'Bank account number is required';
      }
      if (!bankDetails.bankName.trim()) {
        errors.bankName = 'Bank name is required';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleMobileDetailChange = (e) => {
    const { name, value } = e.target;
    setMobileDetails(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleBankDetailChange = (e) => {
    const { name, value } = e.target;
    setBankDetails(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const values = {
        amount: Number(formData.amount),
        drEntityAccount: formData.wallet,
        externalSettlementReference: `SETT-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        settlementRemarks: formData.remarks.trim() || null,
      };

      await requestSettlement(values);

      modalClose();
      show_toast('Settlement request submitted successfully!', 'success');

      // Reset form
      setFormData({ wallet: '', amount: '', remarks: '', channel: 'mobile', mobileType: 'phone' });
      setMobileDetails({ phoneNumber: '', businessNo: '', accountNo: '', tillNo: '' });
      setBankDetails({ bankAccount: '', bankName: '' });

    } catch (err) {
      console.error('Settlement request failed:', err);
      modalClose();
      show_toast(`Settlement request failed: ${err.message || 'Please try again'}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style jsx>{`
        .settlement-container {
          max-width: 100%;
          width: 100%;
          margin: 0 auto;
          padding: 32px;
          background: #fffff;
          border-radius: 16px;
          box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.12);
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .settlement-title {
          font-size: 1.75rem;
          font-weight: 700;
          text-align: center;
          margin: 0 0 32px;
          color: #111827;
        }

        .balance-info {
          background: #f0fdf4;
          border: 1px solid #86efac;
          border-radius: 10px;
          padding: 12px 16px;
          margin: 16px 0;
          font-size: 1rem;
          color: #166534;
          font-weight: 600;
        }

        .form-group {
          margin-bottom: 10px;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
        }

        .form-input,
        .form-select,
        .form-textarea {
          width: 100%;
          padding: 14px 16px;
          border: 1px solid #d1d5db;
          border-radius: 10px;
          font-size: 1.05rem;
          background-color: #ffffff;
          transition: all 0.2s ease;
          box-sizing: border-box;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #10b981;
          box-shadow: 0 0 0 5px rgba(16, 185, 129, 0.2);
        }

        .form-select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.8' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 16px center;
          background-repeat: no-repeat;
          background-size: 14px;
        }

        .form-textarea {
          min-height: 110px;
          resize: vertical;
        }

        .error-text {
          color: #dc2626;
          font-size: 0.9rem;
          margin-top: 8px;
          font-weight: 500;
        }

        .radio-group {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-top: 12px;
        }

        .radio-label {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 1.05rem;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
        }

        .radio-label input[type="radio"] {
          width: 20px;
          height: 20px;
          accent-color: #10b981;
        }

        .channel-section {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
          margin-top: 12px;
        }

        .section-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #111827;
          margin-bottom: 16px;
        }

        .submit-button {
          width: 100%;
          padding: 16px;
          margin-top: 32px;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 1.15rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s ease;
        }

        .submit-button:hover:not(:disabled) {
          background: #059669;
        }

        .submit-button:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }
      `}</style>

      <div className="settlement-container">
        <h2 className="settlement-title">Request Settlement</h2>

        <form onSubmit={handleSubmit} noValidate>
          {/* Wallet Selection */}
          <div className="form-group">
            <label className="form-label">Select Wallet</label>
            {isLoading ? (
              <select className="form-select" disabled>
                <option>Loading wallets...</option>
              </select>
            ) : wallets.length === 0 ? (
              <select className="form-select" disabled>
                <option>No wallets available</option>
              </select>
            ) : (
              <select
                className="form-select"
                name="wallet"
                value={formData.wallet}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="">— Choose a wallet —</option>
                {wallets.map(w => (
                  <option key={w.accountNumber} value={w.accountNumber}>
                    {w.accountName || w.accountNumber} (KES {Number(w.accountBalance ?? w.balance ?? 0).toLocaleString()})
                  </option>
                ))}
              </select>
            )}
            {formErrors.wallet && <div className="error-text">{formErrors.wallet}</div>}
          </div>

          {/* Available Balance */}
          {selectedWallet && (
            <div className="balance-info">
              Available Balance: KES {Number(availableBalance).toLocaleString()}
            </div>
          )}

          {/* Amount */}
          <div className="form-group">
            <label className="form-label">Amount (KES)</label>
            <input
              className="form-input"
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="Enter amount"
              step="1"
              min="1"
              disabled={isSubmitting || !formData.wallet}
              required
            />
            {formErrors.amount && <div className="error-text">{formErrors.amount}</div>}
          </div>

          {/* Remarks */}
          <div className="form-group">
            <label className="form-label">Remarks (Optional)</label>
            <textarea
              className="form-textarea"
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              placeholder="Add a reference or note (max 160 characters)"
              maxLength={160}
              disabled={isSubmitting}
            />
          </div>

          {/* Main Channel: Mobile or Bank */}
          <div className="form-group">
            <label className="form-label">Payout Channel</label>
            <div className="radio-group" style={{ flexDirection: 'row', gap: '40px' }}>
              <label className="radio-label">
                <input
                  type="radio"
                  name="channel"
                  value="mobile"
                  checked={formData.channel === 'mobile'}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                M-Pesa
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="channel"
                  value="bank"
                  checked={formData.channel === 'bank'}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                Bank Transfer
              </label>
            </div>
          </div>

          {/* Channel Details */}
          {formData.channel && (
            <div className="channel-section">
              <div className="section-title">
                {formData.channel === 'mobile' ? 'M-Pesa Payout Type' : 'Bank Details'}
              </div>

              {formData.channel === 'mobile' && (
                <>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="mobileType"
                        value="phone"
                        checked={formData.mobileType === 'phone'}
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                      Send to Phone Number
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="mobileType"
                        value="paybill"
                        checked={formData.mobileType === 'paybill'}
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                      PayBill
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="mobileType"
                        value="till"
                        checked={formData.mobileType === 'till'}
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                      Buy Goods (Till Number)
                    </label>
                  </div>

                  {/* Mobile sub-fields */}
                  {formData.mobileType === 'phone' && (
                    <div className="form-group" style={{ marginTop: '20px' }}>
                      <label className="form-label">Phone Number</label>
                      <input
                        className="form-input"
                        type="tel"
                        name="phoneNumber"
                        placeholder="e.g. 0712345678 or +254712345678"
                        value={mobileDetails.phoneNumber}
                        onChange={handleMobileDetailChange}
                        disabled={isSubmitting}
                      />
                      {formErrors.phoneNumber && <div className="error-text">{formErrors.phoneNumber}</div>}
                    </div>
                  )}

                  {formData.mobileType === 'paybill' && (
                    <>
                      <div className="form-group" style={{ marginTop: '20px' }}>
                        <label className="form-label">Business Number (PayBill)</label>
                        <input
                          className="form-input"
                          type="text"
                          name="businessNo"
                          placeholder="e.g. 123456"
                          value={mobileDetails.businessNo}
                          onChange={handleMobileDetailChange}
                          disabled={isSubmitting}
                        />
                        {formErrors.businessNo && <div className="error-text">{formErrors.businessNo}</div>}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Account Number (Optional)</label>
                        <input
                          className="form-input"
                          type="text"
                          name="accountNo"
                          placeholder="Account reference"
                          value={mobileDetails.accountNo}
                          onChange={handleMobileDetailChange}
                          disabled={isSubmitting}
                        />
                      </div>
                    </>
                  )}

                  {formData.mobileType === 'till' && (
                    <div className="form-group" style={{ marginTop: '20px' }}>
                      <label className="form-label">Till Number</label>
                      <input
                        className="form-input"
                        type="text"
                        name="tillNo"
                        placeholder="e.g. 123456"
                        value={mobileDetails.tillNo}
                        onChange={handleMobileDetailChange}
                        disabled={isSubmitting}
                      />
                      {formErrors.tillNo && <div className="error-text">{formErrors.tillNo}</div>}
                    </div>
                  )}
                </>
              )}

              {formData.channel === 'bank' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Bank Account Number</label>
                    <input
                      className="form-input"
                      type="text"
                      name="bankAccount"
                      placeholder="e.g. 0123456789"
                      value={bankDetails.bankAccount}
                      onChange={handleBankDetailChange}
                      disabled={isSubmitting}
                    />
                    {formErrors.bankAccount && <div className="error-text">{formErrors.bankAccount}</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Bank Name</label>
                    <input
                      className="form-input"
                      type="text"
                      name="bankName"
                      placeholder="e.g. Equity Bank, KCB"
                      value={bankDetails.bankName}
                      onChange={handleBankDetailChange}
                      disabled={isSubmitting}
                    />
                    {formErrors.bankName && <div className="error-text">{formErrors.bankName}</div>}
                  </div>
                </>
              )}
            </div>
          )}

          <button
            className="submit-button"
            type="submit"
            disabled={isLoading || wallets.length === 0 || isSubmitting}
          >
            {isSubmitting ? 'Submitting Request...' : 'Submit Request'}
          </button>
        </form>
      </div>
    </>
  );
}

export default RequestSettlement;